import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-hot-toast";
import cn from "classnames";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import TextInput from "../Settings/TextInput";
import TextArea from "../Settings/TextArea";
import DatePicker from "../Settings/DatePicker";
import Toggle from "../Settings/Toggle";
import { api, callParties, CallListItem, downloadFile } from "./api";

type Format = "letter" | "legal" | "txt" | "docx";

interface Props {
  open: boolean;
  onClose: () => void;
  investigationId: string;
  defaultCaseId: string;
  // Single-call mode (from a call's detail page): one file, custom title.
  callId?: string;
  defaultTitle?: string;
  // Multi-call mode (from the Export menu): pick which calls to include.
  // Each file is titled with its recording name; more than one downloads
  // as a ZIP of individually rendered transcripts.
  calls?: CallListItem[];
  callsLoading?: boolean;
}

// Mirrors the case editor's "Export Transcription" dialog so a detective gets
// the same options (case ID, title, date, PDF/TXT, timestamps, header) for a
// call transcript as for interaction audio on a case.
const ExportTranscriptModal: React.FC<Props> = ({ open, onClose, investigationId, callId, defaultCaseId, defaultTitle, calls, callsLoading }) => {
  const multi = !callId;
  const [format, setFormat] = useState<Format>("letter");
  const [customCaseId, setCustomCaseId] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customHeader, setCustomHeader] = useState("");
  const [customDate, setCustomDate] = useState(moment().format("YYYY-MM-DD"));
  const [includeTimestamps, setIncludeTimestamps] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Only calls with a transcript on file, oldest first (the Calls tab
  // orders by relevance, which is the wrong order for a packet).
  const exportable = useMemo(
    () =>
      (calls || [])
        .filter((c) => c.transcript_s3_key && !c.file.privileged_at)
        .sort((a, b) => (a.call_started_at ? new Date(a.call_started_at).getTime() : Infinity) - (b.call_started_at ? new Date(b.call_started_at).getTime() : Infinity)),
    [calls]
  );
  // Everything starts checked: the common case is "all of them".
  useEffect(() => {
    if (open && multi) setSelected(new Set(exportable.map((c) => c.id)));
  }, [open, multi, exportable]);

  const reset = () => {
    setFormat("letter");
    setCustomCaseId("");
    setCustomTitle("");
    setCustomHeader("");
    setCustomDate(moment().format("YYYY-MM-DD"));
    setIncludeTimestamps(true);
    setResetKey((k) => k + 1);
  };
  const close = () => {
    onClose();
    reset();
  };

  const count = multi ? selected.size : 1;
  const ext = format === "txt" ? "txt" : format === "docx" ? "docx" : "pdf";

  const handleExport = async () => {
    if (multi && !count) return;
    setExporting(true);
    try {
      const common = { format, customCaseId: customCaseId || defaultCaseId, customHeader, customDate, includeTimestamps };
      if (multi) {
        await downloadFile(api.transcriptsExportUrl(investigationId), count > 1 ? "transcripts.zip" : `transcript.${ext}`, { ...common, callIds: [...selected] });
        toast.success(count > 1 ? `${count} transcripts downloaded` : `Transcript ${ext.toUpperCase()} downloaded`);
      } else {
        await downloadFile(api.transcriptExportUrl(investigationId, callId!), `transcript.${ext}`, {
          ...common,
          customTitle: customTitle || defaultTitle,
          // server falls back to the investigation case number / file name
        });
        toast.success(`Transcript ${ext.toUpperCase()} downloaded`);
      }
      close();
    } catch (e: any) {
      toast.error(e.message || "Failed to export transcript");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <dialog ref={dialogRef} className="modal" onCancel={(e) => { e.preventDefault(); close(); }}>
      <div className={cn("modal-box prose w-11/12 p-8", multi ? "md:w-7/12 max-w-3xl" : "md:w-5/12")}>
        <h2>{multi ? "Export Transcriptions" : "Export Transcription"}</h2>
        <p className="mb-4">
          Speaker labels in {multi ? "these transcriptions" : "this transcription"} are generated automatically and may not be 100% accurate. It is the
          responsibility of the reviewing officer to verify and differentiate between speakers to ensure the accuracy of
          the narrative.
        </p>
        <p className="mb-4">
          By exporting {multi ? "these transcription files" : "this transcription file"}, you acknowledge the potential limitations of automated speaker
          identification and agree to review the content for accuracy.
        </p>

        {multi && (
          <div className="mb-4 not-prose">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-base-content">
                Calls
                <span className="ml-1.5 text-base-content/60 font-normal">
                  {count} of {exportable.length} selected
                </span>
              </span>
              <span className="text-xs">
                <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setSelected(new Set(exportable.map((c) => c.id)))}>
                  Select all
                </button>
                <span className="text-base-content/40 mx-1.5">·</span>
                <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setSelected(new Set())}>
                  None
                </button>
              </span>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
              {callsLoading ? (
                <div className="p-3 text-sm text-base-content/60">Loading calls…</div>
              ) : exportable.length === 0 ? (
                <div className="p-3 text-sm text-base-content/60">No calls have a transcript yet.</div>
              ) : (
                exportable.map((c) => {
                  const parties = callParties(c);
                  const checked = selected.has(c.id);
                  return (
                    <label key={c.id} className={cn("flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200", checked && "bg-blue-50/60 dark:bg-blue-900/20")}>
                      {/* App blue (blue-500, the primary button color), not DaisyUI's
                          `checkbox-primary` theme blue. Same idiom as Subscription.tsx. */}
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
                        checked={checked}
                        onChange={() => toggle(c.id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-base-content truncate">
                          {c.call_started_at ? moment(c.call_started_at).format("MMM D, YYYY h:mm A") : "Date unknown"}
                          <span className="text-base-content/40 mx-1.5">·</span>
                          {parties.primary}
                          {parties.secondary && (
                            <>
                              <span className="text-base-content/40 mx-1.5">→</span>
                              {parties.secondary}
                            </>
                          )}
                        </span>
                        <span className="block text-[11px] text-base-content/50 truncate">{c.file.original_file_name}</span>
                      </span>
                      {c.duration_ms != null && <span className="text-xs text-base-content/50 tabular-nums shrink-0">{moment.utc(c.duration_ms).format(c.duration_ms >= 3600000 ? "H:mm:ss" : "m:ss")}</span>}
                    </label>
                  );
                })
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-base-content/50">
              Each transcript is titled with its recording name. Choosing more than one downloads a ZIP of individual files.
            </p>
          </div>
        )}

        <div className="mb-4">
          <TextInput
            key={`case-id-${resetKey}`}
            label="Case ID"
            placeholder="Enter custom case ID (optional)"
            value={customCaseId}
            onSave={setCustomCaseId}
            action={customCaseId || defaultCaseId || "Default Case ID"}
            size="sm"
          />
        </div>
        {!multi && (
          <div className="mb-4">
            <TextInput
              key={`title-${resetKey}`}
              label="Title"
              placeholder="Enter custom title (optional)"
              value={customTitle}
              onSave={setCustomTitle}
              action={customTitle || defaultTitle || "Default Title"}
              size="sm"
            />
          </div>
        )}
        <div className="mb-4">
          <DatePicker key={`date-${resetKey}`} label="Date" value={customDate} onChange={setCustomDate} size="sm" />
        </div>
        <div className="mb-4">
          <Dropdown
            label="Export Format"
            options={[
              { value: "letter", label: 'PDF - Letter (8.5" x 11")' },
              { value: "legal", label: 'PDF - Legal (8.5" x 14")' },
              { value: "docx", label: "DOCX - Word Document" },
              { value: "txt", label: "TXT - Text File" },
            ]}
            value={format}
            onChange={(v) => setFormat(v as Format)}
            className="w-full"
            size="sm"
          />
        </div>
        <div className="mb-4">
          <Toggle label="Include Timestamps" checked={includeTimestamps} onChange={(e) => setIncludeTimestamps(e.target.checked)} />
        </div>
        <div className="mb-4">
          <TextArea
            key={`header-${resetKey}`}
            label="Header"
            placeholder="Enter custom header text (optional)"
            value={customHeader}
            onChange={(e) => setCustomHeader(e.target.value)}
            rows={4}
          />
        </div>
        <div className="modal-action">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExport} disabled={exporting || (multi && !count)}>
            {exporting ? "Generating..." : multi && count > 1 ? `Download ${count} files (ZIP)` : "Download file"}
          </Button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={close}>close</button>
      </form>
    </dialog>
  );
};

export default ExportTranscriptModal;
